import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { ClientId, RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api';
import {
	ConfluencePageCreateCommand,
	ConfluenceUpdatePageCommand,
	ConfluencePageInitCommand,
	ConfluenceDeepResearchPageCreateCommand,
	ConfluenceSubmitPromptOnPageCreateCommand,
	PrepareContentForFinalizationCommand,
	ReadContentCommand,
	RovoStreamAbortedCommand,
} from '@atlassian/rovo-content-bridge-api-commands';

import { ConfluencePageCreateCommandProcessor } from '../stream-processors/create-command-processor';
import { ConfluencePageUpdateCommandProcessor } from '../stream-processors/update-command-processor';
import type {
	ICommandHandler,
	IStreamingCommandHandler,
	PassThroughCommandProperties,
} from '../command-handlers/types/command-handler-types';
import { CreateCommandHandler } from '../command-handlers/create-command-handler';
import { DeepResearchCommandHandler } from '../command-handlers/deep-research-command-handler';
import { PageInitCommandHandler } from '../command-handlers/page-init-command-handler';
import { UpdateCommandHandler } from '../command-handlers/update-command-handler';
import { AbortCommandHandler } from '../command-handlers/abort-command-handler';
import { PrepareContentForFinalizationCommandHandler } from '../command-handlers/prepare-content-for-finalization-command-handler';
import { ReadContentCommandHandler } from '../command-handlers/read-content-command-handler';
import { SubmitPromptCommandHandler } from '../command-handlers/submit-prompt-command-handler';

import type { ContentContext } from './types/command-context-types';

export class CommandManager {
	private commandHandlers: Map<any, ICommandHandler> = new Map();
	private streamingCommands: IStreamingCommandHandler[] = [];
	private createCommandProcessor: ConfluencePageCreateCommandProcessor;
	private updateCommandProcessor: ConfluencePageUpdateCommandProcessor;

	constructor(
		private editorAPI: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
		private contentContext: ContentContext,
		private clientId: ClientId,
		private rovoBridgeAPI: RovoContentBridgeAPI,
	) {
		this.createCommandProcessor = new ConfluencePageCreateCommandProcessor(
			this.editorAPI,
			this.contentContext,
		);
		this.updateCommandProcessor = new ConfluencePageUpdateCommandProcessor(
			this.editorAPI,
			this.contentContext,
			this.clientId,
			this.rovoBridgeAPI,
		);
	}

	registerCommands(properties: PassThroughCommandProperties) {
		// Streaming commands
		this.registerCreateCommandHandler(properties);
		this.registerUpdateCommandHandler(properties);
		this.registerDeepResearchCommandHandler(properties);

		// This is technically a non-streaming command, as we only
		// receive a single message, but it's designed to function
		// similarly to a streaming command.
		this.registerPageInitCommandHandler(properties);

		// Non-streaming commands
		this.registerReadCommandHandler(properties);
		this.registerPrepareContentForFinalizationCommandHandler(properties);
		this.registerSubmitPromptCommandHandler(properties);
		this.registerAbortCommandHandler(properties);
	}

	reset() {
		this.streamingCommands = [];
		this.commandHandlers.clear();
	}

	getCommandHandlers(): Map<any, ICommandHandler> {
		return this.commandHandlers;
	}

	private registerCreateCommandHandler(properties: PassThroughCommandProperties) {
		const createCommandHandler = new CreateCommandHandler(properties, this.createCommandProcessor);
		this.registerCommandHandler(ConfluencePageCreateCommand, createCommandHandler);
		this.streamingCommands.push(createCommandHandler);
	}

	private registerUpdateCommandHandler(properties: PassThroughCommandProperties) {
		const updateCommandHandler = new UpdateCommandHandler(properties, this.updateCommandProcessor);
		this.registerCommandHandler(ConfluenceUpdatePageCommand, updateCommandHandler);
		this.streamingCommands.push(updateCommandHandler);
	}

	private registerPageInitCommandHandler(properties: PassThroughCommandProperties) {
		const pageInitCommandHandler = new PageInitCommandHandler(properties);
		this.registerCommandHandler(ConfluencePageInitCommand, pageInitCommandHandler);
	}

	private registerDeepResearchCommandHandler(properties: PassThroughCommandProperties) {
		const deepResearchCommandHandler = new DeepResearchCommandHandler(properties);
		this.registerCommandHandler(
			ConfluenceDeepResearchPageCreateCommand,
			deepResearchCommandHandler,
		);
		this.streamingCommands.push(deepResearchCommandHandler);
	}

	private registerReadCommandHandler(properties: PassThroughCommandProperties) {
		const readCommandHandler = new ReadContentCommandHandler(properties);
		this.registerCommandHandler(ReadContentCommand, readCommandHandler);
	}

	private registerPrepareContentForFinalizationCommandHandler(
		properties: PassThroughCommandProperties,
	) {
		const prepareFinalizationCommandHandler = new PrepareContentForFinalizationCommandHandler(
			properties,
		);
		this.registerCommandHandler(
			PrepareContentForFinalizationCommand,
			prepareFinalizationCommandHandler,
		);
	}

	private registerSubmitPromptCommandHandler(properties: PassThroughCommandProperties) {
		const submitPromptCommandHandler = new SubmitPromptCommandHandler(properties);
		this.registerCommandHandler(
			ConfluenceSubmitPromptOnPageCreateCommand,
			submitPromptCommandHandler,
		);
	}

	private registerAbortCommandHandler(properties: PassThroughCommandProperties) {
		const abortCommandHandler = new AbortCommandHandler(properties, () => {
			this.streamingCommands.forEach((cmd) => cmd.abort());
			properties.setEditorDisabled(false);
		});
		this.registerCommandHandler(RovoStreamAbortedCommand, abortCommandHandler);
	}

	private registerCommandHandler(command: any, handler: ICommandHandler) {
		this.commandHandlers.set(command, handler);
	}
}
